/**
 * Analytics Utility for Google Analytics 4 and Meta (Facebook) Pixel
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    fbq: any;
    _fbq: any;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const FB_ID = import.meta.env.VITE_FB_PIXEL_ID;

/**
 * Initialize Analytics Scripts
 */
export const initAnalytics = () => {
  if (typeof window === 'undefined') return;

  // Initialize GA4
  if (GA_ID) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID);
    console.log('GA4 Initialized');
  }

  // Initialize Meta Pixel
  if (FB_ID) {
    (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', FB_ID);
    window.fbq('track', 'PageView');
    console.log('Meta Pixel Initialized');
  }
};

/**
 * Track Page View
 */
export const trackPageView = (url: string) => {
  if (GA_ID && window.gtag) {
    window.gtag('config', GA_ID, { page_path: url });
  }
  if (FB_ID && window.fbq) {
    window.fbq('track', 'PageView');
  }
};

/**
 * Track Custom Event
 */
export const trackEvent = (eventName: string, params: Record<string, any> = {}) => {
  if (GA_ID && window.gtag) {
    window.gtag('event', eventName, params);
  }
  if (FB_ID && window.fbq) {
    // Map common GA events to FB events if necessary
    const fbEvent = eventName === 'generate_lead' ? 'Lead' : 
                    eventName === 'contact' ? 'Contact' : 
                    eventName === 'view_item' ? 'ViewContent' : 'CustomEvent';
    
    window.fbq('trackCustom', eventName, params);
    if (fbEvent !== 'CustomEvent') {
      window.fbq('track', fbEvent, params);
    }
  }
  
  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Analytics] Event: ${eventName}`, params);
  }
};

/**
 * Track Lead Submission
 */
export const trackLead = (params: Record<string, any> = {}) => {
  trackEvent('generate_lead', params);
};

/**
 * Track Button Click
 */
export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location
  });
};

/**
 * Track Scroll Depth
 */
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', {
    depth_percentage: depth
  });
};
